/**
 * Geocoding utility functions for address and coordinate resolution
 */

import { API_ENDPOINTS, getAuthHeaders } from 'constants/config';

export interface LocationResult {
  locality: string;
  country: string;
}

/**
 * Check if a string is in coordinates format (lat,long)
 */
export function isCoordinates(value: string): boolean {
  const coordPattern = /^-?\d+\.?\d*\s*,\s*-?\d+\.?\d*$/;
  return coordPattern.test(value.trim());
}

/**
 * Check if a country value includes "United Kingdom"
 */
export function isCountryInUnitedCountries(value: string): boolean {
  return value.toLocaleLowerCase().includes("United Kingdom".toLocaleLowerCase());
}

/**
 * Generic address resolution function
 */
export async function resolveAddress(
  url: string,
  host: string,
  token?: string
): Promise<LocationResult> {
  try {
    const response = await fetch(host + url, {
      method: "GET",
      headers: getAuthHeaders(token)
    });

    if (!response.ok) {
      throw new Error(`API returned status ${response.status}`);
    }

    const data = await response.json();
    const location = {
      locality: data.locality || data.city || data.town || 'Unknown',
      country: data.country || 'Unknown'
    };

    return location;
  } catch (error) {
    console.error('Failed to resolve address:', error);
    return { locality: 'Unknown', country: 'Unknown' };
  }
}

/**
 * Resolve coordinates to a location with caching support
 */
export async function resolveCoordinates(
  coordinates: string,
  host: string,
  token?: string,
  cache?: Map<string, LocationResult>,
  onCacheUpdate?: (cache: Map<string, LocationResult>) => void
): Promise<LocationResult> {
  try {
    // Check cache first
    if (cache?.has(coordinates)) {
      const cachedLocation = cache.get(coordinates);
      if (cachedLocation) {
        return cachedLocation;
      }
    }

    const location = await resolveAddress(
      `${API_ENDPOINTS.GEOCODE_COORDINATES}?latlng=${encodeURIComponent(coordinates)}`,
      host,
      token
    );

    // Cache the result
    if (cache && onCacheUpdate) {
      const newCache = new Map(cache);
      newCache.set(coordinates, location);
      onCacheUpdate(newCache);
    }

    return location;
  } catch (error) {
    console.error('Failed to resolve coordinates:', error);
    return { locality: 'Unknown', country: 'Unknown' };
  }
}

/**
 * Resolve partial UK address to full location
 */
export async function resolveAddressInUnitedCountries(
  partialAddress: string,
  host: string,
  token?: string,
  cache?: Map<string, LocationResult>,
  onCacheUpdate?: (cache: Map<string, LocationResult>) => void
): Promise<LocationResult> {
  try {
    // Check cache first
    if (cache?.has(partialAddress)) {
      const cachedLocation = cache.get(partialAddress);
      if (cachedLocation) {
        return cachedLocation;
      }
    }

    const location = await resolveAddress(
      `${API_ENDPOINTS.GEOCODE_COORDINATES}/${encodeURIComponent(partialAddress)}`,
      host,
      token
    );

    // Cache the result
    if (cache && onCacheUpdate) {
      const newCache = new Map(cache);
      newCache.set(partialAddress, location);
      onCacheUpdate(newCache);
    }

    return location;
  } catch (error) {
    console.error('Failed to resolve address:', error);
    return { locality: 'Unknown', country: 'Unknown' };
  }
}
